import axios from "axios";
import { API_BASE_URL } from "../../config/apiConfig";

// Create axios instance with auth
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const user = JSON.parse(localStorage.getItem("user"));
  if (user?.token) {
    config.headers.Authorization = `Bearer ${user.token}`;
  }
  return config;
});

// Refresh expired media URLs automatically
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (
      error.response?.status === 403 &&
      error.response?.data?.message?.includes("URL expired")
    ) {
      // Retry with fresh URL
      const originalRequest = error.config;
      const postId = extractPostIdFromUrl(originalRequest.url);
      if (postId) {
        await feedApi.getPost(postId);
        return apiClient(originalRequest);
      }
    }
    return Promise.reject(error);
  }
);

// Helper to extract post ID from URL
const extractPostIdFromUrl = (url) => {
  const match = url.match(/\/posts\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
};

export const feedApi = {
  /**
   * GET FEED
   * Endpoint: GET /api/posts/feed
   * Params: page, limit, filter, feedType, cursor, optimized
   */
  getFeed: async ({
    page = 1,
    limit = 10,
    filter = null, // 'research', 'questions', 'photos', 'collaboration'
    feedType = "personalized", // 'personalized', 'following', 'trending'
    cursor = null,
    topic = null,
    optimized = true,
  } = {}) => {
    return apiClient.get("/posts/feed", {
      params: { page, limit, filter, feedType, cursor, topic, optimized },
    });
  },

  /**
   * GET SINGLE POST
   * Endpoint: GET /api/posts/:id
   */
  getPost: async (postId) => {
    return apiClient.get(`/posts/${postId}`);
  },

  /**
   * PREFETCH NEXT PAGE (Performance Optimization)
   * Endpoint: POST /api/posts/feed/prefetch
   * Body: { cursor }
   */
  prefetchNextPage: async (cursor) => {
    return apiClient.post("/posts/feed/prefetch", { cursor });
  },

  /**
   * GET USER POSTS
   * Endpoint: GET /api/posts/user/:userId
   */
  getUserPosts: async (
    userId,
    { page = 1, limit = 10, filter = null } = {}
  ) => {
    return apiClient.get(`/posts/user/${userId}`, {
      params: { page, limit, filter },
    });
  },
};

export default feedApi;
